import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {Router} from '@angular/router';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICheckoutComponent} from '@app/checkout/models/checkout-component.model';
import {IConsentPdfState} from '@app/checkout/models/consent-pdf-state.model';
import {Order} from '@app/checkout/models/order-request.model';
import {consentPdfAction, resetCheckoutStatus, updateOrderFromConsentInfo} from '@app/checkout/state/checkout.actions';
import {selectCheckoutStatus, selectConsentPdfState, selectOrderForm} from '@app/checkout/state/checkout.selector';
import {CommonUtils} from '@app/core/utils/common.utils';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {StepperService} from '@app/shared/services/stepper.service';
import {selectActiveLanguages} from '@app/shared/state/language/language.selector';
import {Store} from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {MessageService, SelectItem} from 'primeng/api';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {IConsentInfoForm} from '../../models/consent-info.model';
import { Bind } from 'primeng/bind';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { RadioButton } from 'primeng/radiobutton';

@Component({
    selector: 'app-consent-info',
    templateUrl: './consent-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Bind,
        Select,
        Button,
        RadioButton,
        TranslatePipe,
    ],
})
export class ConsentInfoComponent implements OnInit, OnDestroy, ICheckoutComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly stepperService = inject(StepperService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  orderData: Order | undefined;
  private readonly unsubscribe$ = new Subject<void>();
  checkoutState: ECheckoutStatus | undefined;
  checkoutStatus = ECheckoutStatus;
  downloadInProgress = false;
  consentForm: FormGroup<IConsentInfoForm>;
  consentOptions: SelectItem[] = [
    {label: 'Yes', value: true},
    {label: 'No', value: false},
  ];
  languageOptions: SelectItem[] = [{label: 'English', value: 1}];
  actionStatus = EActionStatus;
  consentPdf$: Observable<IConsentPdfState>;
  nextInProgress = false;
  previousInProgress = false;
  cancelInProgress = false;
  draftInProgress = false;
  globalToast = 'global-toast';

  constructor() {
    this.consentForm = this.formBuilder.group({
      languageId: [1],
      consent: [false],
      sampleConsent: new FormControl<boolean | null>(null, Validators.required),
    });

    this.consentPdf$ = this.store.select(selectConsentPdfState);

    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.orderData = order;
          this.consentForm.patchValue({
            consent: this.orderData.consentAccepted,
            sampleConsent: this.orderData.sampleConsentAccepted,
          });
        })
      )
      .subscribe();

    this.store
      .select(selectActiveLanguages)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(languages => {
          if (languages) {
            this.languageOptions = languages.map(x => ({label: x.name, value: x.id}) as SelectItem);
          }
        })
      )
      .subscribe();

    this.getCheckoutStatus();
  }

  ngOnInit(): void {
    this.consentPdf$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((state: IConsentPdfState) => {
          if (state.status !== EActionStatus.Initial && state.status !== EActionStatus.Pending) {
            this.downloadInProgress = false;
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onDownloadConsent(): void {
    const languageId = this.consentForm.get('languageId')?.value;
    const productId = this.orderData?.orderItems[0]?.productId;
    if (languageId && productId) {
      this.downloadInProgress = true;
      this.store.dispatch(
        consentPdfAction({
          request: {
            orderId: this.orderData?.id ?? 0,
            productId: productId,
            languageId: languageId,
          },
        })
      );
    }
  }

  disableNext() {
    return this.isConsentFormInvalid();
  }

  isConsentFormInvalid() {
    return (
      !this.consentForm.controls.consent.value ||
      this.consentForm.controls.sampleConsent.value === undefined ||
      this.consentForm.controls.sampleConsent.value === null
    );
  }

  next() {
    const lastCompletedStep = this.orderData?.lastCompletedStep ?? -1;
    if (this.consentForm.dirty || lastCompletedStep < CheckoutStep.ConsentInfo) {
      this.nextInProgress = true;
      if (this.orderData) {
        this.orderData = {
          ...this.orderData,
          lastCompletedStep:
            lastCompletedStep > CheckoutStep.ConsentInfo ? lastCompletedStep : CheckoutStep.ConsentInfo,
          currentStep: CheckoutStep.ConfirmOrder,
          orderStatusId: OrderStatusEnum.New.toString(),
        };
      }
      this.store.dispatch(resetCheckoutStatus());
      this.saveOrder();
    } else {
      this.stepperService.emitNext(CheckoutStep.ConfirmOrder);
    }
  }

  previous() {
    this.stepperService.emitPrevious(CheckoutStep.ClinicalHistory);
  }

  cancelOrder() {
    // use for custom cancel logic
  }

  saveDraftOrder() {
    if (this.orderData && this.orderData.lastCompletedStep >= CheckoutStep.ConsentInfo && this.isConsentFormInvalid()) {
      ErrorHandlerUtils.showErrorMessage(
        this.messageService,
        this.globalToast,
        this.translate.instant('Checkout.ConsentInfoRequired')
      );
      return;
    }
    this.draftInProgress = true;
    if (this.orderData) {
      this.orderData = {
        ...this.orderData,
        orderStatusId: OrderStatusEnum.New.toString(),
      };
    }
    this.saveOrder(true);
  }

  nextLoading() {
    return this.nextInProgress;
  }

  previousLoading() {
    return this.previousInProgress;
  }

  cancelLoading() {
    return this.cancelInProgress;
  }

  draftOrSubmitLoading() {
    return this.draftInProgress;
  }

  saveOrder(isDraft = false) {
    let orderData = {
      ...this.orderData,
      productId: this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId,
      consentAccepted: CommonUtils.isNullOrUndefined(this.consentForm.get('consent')?.value)
        ? null
        : this.consentForm.get('consent')?.value,
      sampleConsentAccepted: CommonUtils.isNullOrUndefined(this.consentForm.controls.sampleConsent.value)
        ? null
        : this.consentForm.controls.sampleConsent.value,
      currentStep: isDraft ? this.orderData?.currentStep : CheckoutStep.ConsentInfo,
    } as Order;

    if (!isDraft) {
      orderData = {
        ...orderData,
        consentRecordStamp: new Date(),
      };
    }

    this.store.dispatch(updateOrderFromConsentInfo({orderRequest: orderData, isDraft: isDraft}));
  }

  private getCheckoutStatus() {
    this.store
      .select(selectCheckoutStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((checkoutStatus: ECheckoutStatus) => {
          this.checkoutState = checkoutStatus;
          if (checkoutStatus === ECheckoutStatus.SuccessFromConsentInfo) {
            this.consentForm.markAsPristine();
            this.stepperService.emitNext(CheckoutStep.ConfirmOrder);
          }
          if (
            checkoutStatus === ECheckoutStatus.SuccessFromConsentInfo ||
            checkoutStatus === ECheckoutStatus.ErrorFromConsentInfo
          ) {
            this.nextInProgress = false;
            this.draftInProgress = false;
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }
}
